import { Button, Card, Stepper } from "@design";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { AskUserAnswerField, answerError } from "./AskUserAnswerField";
import { AskUserOptions } from "./AskUserOptions";

export interface AskUserQuestion {
  question: string;
  header?: string;
  options?: { label: string; description?: string }[];
  multiple?: boolean;
  allow_custom?: boolean;
  /** Free-text field type; defaults to a one-line text box. */
  input_type?: "text" | "number" | "date" | "email" | "url" | "textarea";
  /** Defaults to true; optional questions can be left blank. */
  required?: boolean;
}

type Answer = { selected: string[]; text: string };

/** More questions than this go one at a time with a Stepper. */
export const STEP_THRESHOLD = 3;

const isAnswered = (a: Answer) => a.selected.length > 0 || Boolean(a.text.trim());

/** This question is answered if required, and any typed answer is in the right format. */
export function questionDone(q: AskUserQuestion, a: Answer): boolean {
  return (q.required === false || isAnswered(a)) && !answerError(q, a.text);
}

/** Every question done, and at least one answer overall. */
export function canSubmit(questions: AskUserQuestion[], answers: Answer[]): boolean {
  return questions.every((q, i) => questionDone(q, answers[i])) && answers.some(isAnswered);
}

/** What the model asked (`ask_user` event): structured questions, or a single free-text question. */
export interface AskUserPrompt {
  question: string;
  questions: unknown[];
}

export function normalizeQuestions(prompt: AskUserPrompt): AskUserQuestion[] {
  const structured = prompt.questions.filter(
    (q): q is AskUserQuestion => Boolean(q) && typeof (q as AskUserQuestion).question === "string",
  );
  if (structured.length > 0) return structured;
  return prompt.question ? [{ question: prompt.question, allow_custom: true }] : [];
}

/** Answers become one message ("Header: answer" per line), the same text frontend/ sends. Skipped optional questions are left out. */
export function answersToMessage(questions: AskUserQuestion[], answers: Answer[]) {
  return questions
    .map((q, i) => ({ q, a: answers[i] }))
    .filter(({ a }) => isAnswered(a))
    .map(({ q, a }) => {
      const answer = a.selected.length > 0 ? [...a.selected, a.text].filter(Boolean).join(", ") : a.text;
      return questions.length === 1 && !q.header ? answer : `${q.header ?? q.question}: ${answer}`;
    })
    .join("\n");
}

/** One question: its options and/or free-text answer. */
function AskUserQuestionField({
  question: q,
  answer: a,
  showError,
  onChange,
  onBlur,
}: Readonly<{
  question: AskUserQuestion;
  answer: Answer;
  showError: boolean;
  onChange: (change: Partial<Answer>) => void;
  onBlur: () => void;
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "askUser" });
  const toggle = (label: string) => {
    let selected = q.multiple ? [...a.selected, label] : [label];
    if (a.selected.includes(label)) selected = a.selected.filter((s) => s !== label);
    onChange({ selected });
  };
  return (
    <fieldset className="space-y-2">
      <legend>
        {q.header && (
          <Text variant="eyebrow" tone="tertiary">
            {q.header}
          </Text>
        )}
        <Text>{q.required === false ? t("optional", { question: q.question }) : q.question}</Text>
      </legend>
      <AskUserOptions question={q} selected={a.selected} onToggle={toggle} />
      {(q.allow_custom !== false || !q.options?.length) && (
        <AskUserAnswerField
          question={q}
          hasOptions={Boolean(q.options?.length)}
          value={a.text}
          showError={showError}
          onChange={(text) => onChange({ text })}
          onBlur={onBlur}
        />
      )}
    </fieldset>
  );
}

/**
 * The agent needs input before it can continue; answering sends a normal message.
 * Long forms (more than STEP_THRESHOLD questions) go one question at a time.
 */
export function AskUserCard({
  prompt,
  onSubmit,
}: Readonly<{ prompt: AskUserPrompt; onSubmit: (text: string) => void }>) {
  const { t } = useTranslation("chat", { keyPrefix: "askUser" });
  const questions = normalizeQuestions(prompt);
  const [answers, setAnswers] = useState<Answer[]>(() => questions.map(() => ({ selected: [], text: "" })));
  const [touched, setTouched] = useState<boolean[]>(() => questions.map(() => false));
  const [step, setStep] = useState(0);
  if (questions.length === 0) return null;

  const update = (index: number, change: Partial<Answer>) =>
    setAnswers((prev) => prev.map((a, i) => (i === index ? { ...a, ...change } : a)));
  const touch = (index: number) => setTouched((prev) => prev.map((v, i) => v || i === index));
  const stepped = questions.length > STEP_THRESHOLD;
  const last = step === questions.length - 1;
  const shown = stepped ? [step] : questions.map((_, i) => i);
  const complete = canSubmit(questions, answers);

  const next = () => {
    touch(step);
    if (questionDone(questions[step], answers[step])) setStep(step + 1);
  };

  return (
    <Card>
      <form
        aria-label={t("label")}
        className="space-y-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (stepped && !last) return next();
          setTouched(questions.map(() => true));
          if (complete) onSubmit(answersToMessage(questions, answers));
        }}
      >
        {shown.map((i) => (
          <AskUserQuestionField
            key={`${questions[i].header ?? ""}|${questions[i].question}`}
            question={questions[i]}
            answer={answers[i]}
            showError={touched[i]}
            onChange={(change) => update(i, change)}
            onBlur={() => touch(i)}
          />
        ))}
        <div className="flex flex-wrap items-center gap-2">
          {stepped && (
            <Stepper
              steps={questions.length}
              currentStep={step}
              label={t("step", { current: step + 1, total: questions.length })}
            />
          )}
          <div className="ml-auto flex gap-2">
            {stepped && step > 0 && (
              <Button type="button" variant="secondary" size="sm" onClick={() => setStep(step - 1)}>
                {t("back")}
              </Button>
            )}
            {/* Distinct keys: reusing one <button> would turn "Next" into submit mid-click and send early. */}
            {stepped && !last ? (
              <Button key="next" type="button" variant="primary" size="sm" onClick={next}>
                {t("next")}
              </Button>
            ) : (
              <Button key="send" type="submit" variant="primary" size="sm" disabled={!complete}>
                {t("send")}
              </Button>
            )}
          </div>
        </div>
      </form>
    </Card>
  );
}
